'use client';

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';
import Link from 'next/link';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { fetchProductMenu } from '@/redux/products/thunk';
import { MenuItem } from '@/redux/products/types';
import { IoArrowForward } from 'react-icons/io5';
import { selectIsLoggedIn } from '@/redux/auth/signin/slice';
import { useAuthGuard } from '@/hooks/useAuthGuard';

const GUTTER = 16;
const HEADER_PANEL_GAP = 4;
const UNDERLINE_GAP = 6;
const UNDERLINE_THICKNESS = 2;
const CARET_GAP = 2;
const HOVER_CLOSE_DELAY = 200;

const getHeaderHeight = (): number => {
  if (typeof window === 'undefined') return 64;
  const css = getComputedStyle(document.documentElement).getPropertyValue(
    '--header-height'
  );
  const parsed = parseInt(css || '0', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 64;
};

export default function ServicesMenu({
  loginPath = '/account/signin',
}: {
  loginPath?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const focusOnOpenRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const caretRef = useRef<HTMLDivElement | null>(null);
  const hoverCloseTimer = useRef<number | null>(null);

  const dispatch = useAppDispatch();
  const { data: menuItems, status: menuStatus } = useAppSelector(
    (s) => s.products.menu
  );
  const isAuthenticated = useAppSelector(selectIsLoggedIn);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (menuStatus === 'idle') {
      dispatch(fetchProductMenu({}));
    }
  }, [dispatch, menuStatus]);

  useEffect(() => {
    if (!isOpen) return;

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  const getHeaderContainerRect = useCallback((): DOMRect => {
    const el = document.querySelector(
      '[data-header-container]'
    ) as HTMLElement | null;
    if (el) return el.getBoundingClientRect();
    return new DOMRect(GUTTER, 0, window.innerWidth - GUTTER * 2, 0);
  }, []);

  const positionPanel = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    const caret = caretRef.current;
    if (!trigger || !panel || !caret) return;

    const headerH = getHeaderHeight();
    const triggerRect = trigger.getBoundingClientRect();
    const desiredCenter = triggerRect.left + triggerRect.width / 2;

    const caretSize = caret.offsetWidth || 16;

    const underlineBottom =
      triggerRect.bottom + UNDERLINE_GAP + UNDERLINE_THICKNESS;
    const seam = Math.max(
      headerH + HEADER_PANEL_GAP / 2,
      underlineBottom + CARET_GAP
    );
    const panelTop = seam + HEADER_PANEL_GAP / 2;

    const containerRect = getHeaderContainerRect();
    const containerLeft = Math.max(GUTTER, containerRect.left);
    const containerWidth = Math.min(
      window.innerWidth - GUTTER * 2,
      containerRect.width || window.innerWidth - GUTTER * 2
    );

    const origVis = panel.style.visibility;
    panel.style.visibility = 'hidden';
    panel.style.display = 'block';

    panel.style.left = `${containerLeft}px`;
    panel.style.top = `${panelTop}px`;
    panel.style.width = `${containerWidth}px`;

    panel.style.visibility = origVis || '';

    const caretLeft = Math.max(
      containerLeft + caretSize / 2,
      Math.min(containerLeft + containerWidth - caretSize / 2, desiredCenter)
    );

    caret.style.left = `${caretLeft - caretSize / 2}px`;
    caret.style.top = `${seam - caretSize / 2}px`;
  }, [getHeaderContainerRect]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    positionPanel();

    const onResize = () => positionPanel();
    const onScroll = () => positionPanel();

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });

    if (focusOnOpenRef.current) {
      focusOnOpenRef.current = false;
      queueMicrotask(() => {
        const firstItem =
          panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
        firstItem?.focus();
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isOpen, menuItems, positionPanel]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) {
        window.clearTimeout(hoverCloseTimer.current);
      }
    };
  }, []);

  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverCloseTimer.current = window.setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY
    ) as unknown as number;
  }, [cancelHoverClose]);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      focusOnOpenRef.current = true;
      setIsOpen((prev) => !prev);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOnOpenRef.current = true;
      setIsOpen(true);
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []
    );
    if (!items.length) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        items[(currentIndex + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
    }
  };

  const panelId = 'services-panel';

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => {
        cancelHoverClose();
        setIsOpen(true);
      }}
    >
      <button
        ref={triggerRef}
        id="services-trigger"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
        onMouseEnter={cancelHoverClose}
        onMouseLeave={scheduleHoverClose}
        className={`
          relative flex items-center gap-1 cursor-pointer whitespace-nowrap px-2 pb-2 text-sm outline-none
          text-[#1b2d2a] hover:text-(--color-accent) active:text-(--color-accent)
          transition-colors duration-200
          after:content-[''] after:absolute after:-bottom-6 after:left-0 after:w-full after:h-0.5
          after:bg-(--color-accent) after:scale-x-0 hover:after:scale-x-100 active:after:scale-x-100
          after:transition-transform after:duration-300 after:origin-left
          focus-visible:ring-2 focus-visible:ring-(--color-accent)/40 focus-visible:rounded
        `}
      >
        <span>Services</span>
        <FaChevronDown
          className={`text-xs transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40!"
              aria-hidden={false}
              onMouseEnter={cancelHoverClose}
              onMouseLeave={scheduleHoverClose}
            >
              <div
                ref={panelRef}
                id={panelId}
                role="menu"
                aria-label="Services"
                aria-labelledby="services-trigger"
                tabIndex={-1}
                onKeyDown={onPanelKeyDown}
                className="
                  fixed z-9998
                  rounded-br-2xl rounded-bl-2xl border border-gray-200 bg-white
                  shadow-[0_12px_30px_-6px_rgba(0,0,0,0.15)]
                  ring-1 ring-black/5
                  px-4 sm:px-6 md:px-10 py-4
                  text-[#1b2d2a]
                  overflow-y-auto
                "
                style={{
                  maxHeight: `calc(100vh - ${getHeaderHeight()}px - 24px)`,
                }}
              >
                {menuStatus === 'loading' ? (
                  <div className="text-center w-full text-gray-400 text-sm py-6">
                    Loading...
                  </div>
                ) : menuStatus === 'failed' ? (
                  <div className="text-center w-full text-red-500 text-sm py-6">
                    Failed to load services. Please try again later.
                  </div>
                ) : menuItems.length === 0 ? (
                  <div className="text-center w-full text-gray-500 text-sm py-6">
                    No services are available at this time.
                  </div>
                ) : (
                  <>
                    <div className="grid gap-x-6 gap-y-10 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
                      {menuItems.map((section) => (
                        <section key={section.id} className="space-y-3 min-w-0">
                          <div className="relative group inline-block">
                            <h4
                              className="font-bold text-[15px] leading-tight tracking-tight"
                              title={section.description || section.name}
                            >
                              {section.name}
                            </h4>
                            {section.description && (
                              <div
                                role="tooltip"
                                className="
                                  pointer-events-none absolute z-10 left-0 mt-1 w-max max-w-88
                                  rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-600
                                  opacity-0 shadow-sm ring-1 ring-black/5 transition-opacity duration-150
                                  group-hover:opacity-100
                                "
                              >
                                {section.description}
                              </div>
                            )}
                          </div>

                          <ul className="space-y-0">
                            {section.subproducts.map((sub) => (
                              <InlineSubMenuItem
                                key={sub.id}
                                item={sub}
                                level={1}
                                rootId={section.id}
                                parentId={section.id}
                                closeMenu={closeMenu}
                                isAuthenticated={isAuthenticated}
                                loginPath={loginPath}
                              />
                            ))}
                          </ul>
                        </section>
                      ))}
                    </div>
                    <div className="h-px w-full bg-linear-to-r from-transparent via-gray-200 to-transparent" />
                  </>
                )}
              </div>
            </div>
            <div
              ref={caretRef}
              aria-hidden
              className="pointer-events-none fixed z-60 h-4 w-4 rotate-45 border border-gray-200 bg-white shadow-sm"
              style={{ left: 0, top: 0 }}
            />
          </>,
          document.body,
        )}
    </div>
  );
}

interface InlineSubMenuItemProps {
  item: MenuItem;
  level: number;
  rootId: string;
  parentId: string;
  closeMenu: () => void;
  isAuthenticated: boolean;
  loginPath?: string;
}

const InlineSubMenuItem: React.FC<InlineSubMenuItemProps> = React.memo(
  ({
    item,
    rootId,
    parentId,
    closeMenu,
    isAuthenticated,
    loginPath = '/account/signin',
  }) => {
    const { guard } = useAuthGuard({
      loginPath,
      onRedirect: closeMenu,
    });

    const href = `/services/application/${rootId}/i/${parentId}/${item.id}`;

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
      (e) => {
        if (guard(!!item.requires_auth, href)) {
          e.preventDefault();
          return;
        }

        closeMenu();
      },
      [item.requires_auth, href, closeMenu, guard]
    );

    return (
      <li className="relative">
        <Link
          href={href}
          role="menuitem"
          tabIndex={0}
          title={item.description || item.name}
          onClick={handleClick}
          className="
            group/link flex items-center justify-between
            text-[13.5px] leading-6 text-[#4a4a4a]
            rounded-md px-1 py-0 transition-all
            hover:bg-gray-50 hover:text-(--color-accent) active:text-(--color-accent)
            hover:underline active:underline
            hover:decoration-(--color-accent) active:decoration-(--color-accent)
            hover:underline-offset-2 active:underline-offset-2
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)/30
          "
        >
          <span className="whitespace-normal wrap-break-word">{item.name}</span>
          <span
            className="
              ml-0 translate-x-0 opacity-0
              group-hover/link:opacity-100 group-hover/link:translate-x-0.5
              group-focus-visible/link:opacity-100 group-focus-visible/link:translate-x-0.5
              group-active/link:opacity-100 group-active/link:translate-x-0.5
              transition-all duration-150
            "
            aria-hidden
            title={
              item.requires_auth && !isAuthenticated
                ? 'Login required'
                : undefined
            }
          >
            <IoArrowForward />
          </span>
        </Link>
      </li>
    );
  }
);

InlineSubMenuItem.displayName = 'InlineSubMenuItem';
